import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, Button, Form, Modal, Badge } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import { Plus, MessageCircle, Link as LinkIcon } from 'lucide-react';
import toast from 'react-hot-toast';

import apiService from '../services/api';
import websocketService from '../services/websocket';
import { useAuth } from '../contexts/AuthContext';

const BrainstormingSession = () => {
  const { projectId, sessionId } = useParams();
  const { user } = useAuth();
  const canvasRef = useRef(null);
  
  const [session, setSession] = useState(null);
  const [ideas, setIdeas] = useState([]);
  const [connections, setConnections] = useState([]);
  const [selectedIdea, setSelectedIdea] = useState(null);
  const [showIdeaModal, setShowIdeaModal] = useState(false);
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [comments, setComments] = useState([]);
  const [newIdea, setNewIdea] = useState({ title: '', content: '', tags: '' });
  const [newComment, setNewComment] = useState('');
  const [draggedIdea, setDraggedIdea] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  useEffect(() => {
    loadSessionData();
    connectWebSocket();

    return () => {
      websocketService.disconnect();
    };
  }, [projectId, sessionId]);

  const loadSessionData = async () => {
    try {
      const [sessionData, ideasData] = await Promise.all([
        apiService.getSession(projectId, sessionId),
        apiService.getIdeas(projectId, sessionId),
      ]);

      setSession(sessionData);
      setIdeas(ideasData);

      // Load connections for all ideas
      const allConnections = [];
      for (const idea of ideasData) {
        const connections = await apiService.getConnections(projectId, idea.id);
        allConnections.push(...connections);
      }
      setConnections(allConnections);
    } catch (error) {
      console.error('Error loading session data:', error);
      toast.error('Failed to load session data');
    }
  };

  const connectWebSocket = () => {
    const socket = websocketService.connect(projectId);
    if (!socket) return;

    websocketService.onIdeaCreated((data) => {
      const idea = data.idea;
      setIdeas(prev => [...prev, {
        id: idea.id,
        title: idea.title,
        content: idea.content,
        x_position: idea.x_position,
        y_position: idea.y_position,
        tags: idea.tags || [],
        creator: { username: idea.creator }
      }]);
      toast.success(`New idea "${idea.title}" added by ${idea.creator}`);
    });

    websocketService.onIdeaUpdated((data) => {
      console.log('DEBUG: idea_updated received:', data);
      const updatedIdea = data.idea;
      if (updatedIdea) {
        setIdeas(prev => prev.map(idea => 
          idea.id === updatedIdea.id ? { ...idea, ...updatedIdea } : idea
        ));
      } else {
        console.error('DEBUG: No idea property in data:', data);
      }
    });

    websocketService.onCommentCreated((data) => {
      const comment = data.comment;
      toast.success(`New comment by ${comment.author}`);
    });

    websocketService.onConnectionCreated((data) => {
      const connection = data.connection;
      setConnections(prev => [...prev, connection]);
    });
  };

  const handleCreateIdea = async (e) => {
    e.preventDefault();
    if (!newIdea.title.trim() || !newIdea.content.trim()) return;

    try {
      const ideaData = {
        title: newIdea.title,
        content: newIdea.content,
        tags: newIdea.tags.split(',').map(tag => tag.trim()).filter(tag => tag),
        x_position: Math.floor(Math.random() * 600 + 50),
        y_position: Math.floor(Math.random() * 400 + 50),
      };

      await apiService.createIdea(projectId, sessionId, ideaData);
      setNewIdea({ title: '', content: '', tags: '' });
      setShowIdeaModal(false);
    } catch (error) {
      console.error('Error creating idea:', error);
      toast.error('Failed to create idea');
    }
  };

  const handleIdeaClick = async (idea) => {
    setSelectedIdea(idea);
    setShowCommentModal(true);
    
    try {
      const commentsData = await apiService.getComments(projectId, idea.id);
      setComments(commentsData);
    } catch (error) {
      console.error('Error loading comments:', error);
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim() || !selectedIdea) return;

    try {
      await apiService.createComment(projectId, selectedIdea.id, { content: newComment });
      setNewComment('');
      
      // Reload comments
      const commentsData = await apiService.getComments(projectId, selectedIdea.id);
      setComments(commentsData);
    } catch (error) {
      console.error('Error adding comment:', error);
      toast.error('Failed to add comment');
    }
  };

  const handleMouseDown = (e, idea) => {
    const rect = canvasRef.current.getBoundingClientRect();
    setDraggedIdea(idea);
    setDragOffset({
      x: e.clientX - rect.left - idea.x_position,
      y: e.clientY - rect.top - idea.y_position,
    });
  };

  const handleMouseMove = (e) => {
    if (!draggedIdea) return;

    const rect = canvasRef.current.getBoundingClientRect();
    const newX = e.clientX - rect.left - dragOffset.x;
    const newY = e.clientY - rect.top - dragOffset.y;

    setIdeas(prev => prev.map(idea => 
      idea.id === draggedIdea.id 
        ? { ...idea, x_position: Math.max(0, newX), y_position: Math.max(0, newY) }
        : idea
    ));
  };

  const handleMouseUp = async () => {
    if (!draggedIdea) return;

    const updatedIdea = ideas.find(idea => idea.id === draggedIdea.id);
    if (updatedIdea) {
      try {
        await apiService.updateIdea(projectId, sessionId, draggedIdea.id, {
          x_position: updatedIdea.x_position,
          y_position: updatedIdea.y_position,
        });
      } catch (error) {
        console.error('Error updating idea position:', error);
      }
    }

    setDraggedIdea(null);
    setDragOffset({ x: 0, y: 0 });
  };

  const renderConnections = () => {
    return connections.map(connection => {
      const fromIdea = ideas.find(idea => idea.id === connection.from_idea_id);
      const toIdea = ideas.find(idea => idea.id === connection.to_idea_id);

      if (!fromIdea || !toIdea) return null;

      const x1 = fromIdea.x_position + 100; // Center of idea node
      const y1 = fromIdea.y_position + 30;
      const x2 = toIdea.x_position + 100;
      const y2 = toIdea.y_position + 30;

      return (
        <line
          key={connection.id}
          x1={x1}
          y1={y1}
          x2={x2}
          y2={y2}
          stroke="#007bff"
          strokeWidth="2"
          strokeDasharray={connection.connection_type === 'related' ? '5,5' : ''}
        />
      );
    });
  };

  if (!session) {
    return <div className="text-center mt-5">Loading...</div>;
  }

  return (
    <Container fluid className="mt-3">
      <Row>
        <Col>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h4>{session.title}</h4>
              <p className="text-muted small">{session.description}</p>
            </div>
            <div className="d-flex gap-2">
              <Button variant="primary" onClick={() => setShowIdeaModal(true)}>
                <Plus size={16} className="me-1" />
                Add Idea
              </Button>
            </div>
          </div>

          <Card>
            <Card.Body className="p-0">
              <div
                ref={canvasRef}
                className="brainstorm-canvas position-relative"
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                style={{ height: '70vh', overflow: 'hidden', cursor: draggedIdea ? 'grabbing' : 'default' }}
              >
                {/* SVG for connections */}
                <svg
                  className="position-absolute"
                  style={{ width: '100%', height: '100%', pointerEvents: 'none', zIndex: 1 }}
                >
                  {renderConnections()}
                </svg>

                {/* Ideas */}
                {ideas.map((idea) => (
                  <div
                    key={idea.id}
                    className={`idea-node ${selectedIdea?.id === idea.id ? 'selected' : ''}`}
                    style={{
                      left: `${idea.x_position}px`,
                      top: `${idea.y_position}px`,
                      zIndex: 10,
                      cursor: draggedIdea ? 'grabbing' : 'grab',
                    }}
                    onMouseDown={(e) => handleMouseDown(e, idea)}
                    onClick={() => handleIdeaClick(idea)}
                  >
                    <div className="idea-title">{idea.title}</div>
                    <div className="idea-content">{idea.content}</div>
                    {idea.tags && idea.tags.length > 0 && (
                      <div className="idea-tags">
                        {idea.tags.map((tag, index) => (
                          <span key={index} className="idea-tag">
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                    <small className="text-muted d-block mt-1">
                      by {idea.creator?.username || 'Unknown'}
                    </small>
                  </div>
                ))}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Add Idea Modal */}
      <Modal show={showIdeaModal} onHide={() => setShowIdeaModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Add New Idea</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleCreateIdea}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Title</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter idea title"
                value={newIdea.title}
                onChange={(e) => setNewIdea({...newIdea, title: e.target.value})}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Content</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Describe your idea"
                value={newIdea.content}
                onChange={(e) => setNewIdea({...newIdea, content: e.target.value})}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Tags (comma-separated)</Form.Label>
              <Form.Control
                type="text"
                placeholder="innovation, technology, user experience"
                value={newIdea.tags}
                onChange={(e) => setNewIdea({...newIdea, tags: e.target.value})}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowIdeaModal(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="primary">
              Add Idea
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Comments Modal */}
      <Modal show={showCommentModal} onHide={() => setShowCommentModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <MessageCircle size={20} className="me-2" />
            {selectedIdea?.title}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <p><strong>Description:</strong> {selectedIdea?.content}</p>
            {selectedIdea?.tags && selectedIdea.tags.length > 0 && (
              <div>
                <strong>Tags:</strong>{' '}
                {selectedIdea.tags.map((tag, index) => (
                  <Badge key={index} bg="secondary" className="me-1">
                    {tag}
                  </Badge>
                ))}
              </div>
            )}
          </div>

          <hr />

          <div className="mb-3">
            <h6>Comments</h6>
            {comments.length === 0 ? (
              <p className="text-muted">No comments yet</p>
            ) : (
              <div className="chat-messages" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                {comments.map((comment) => (
                  <div key={comment.id} className="message mb-2">
                    <div className="message-author">{comment.author.username}</div>
                    <div className="message-content">{comment.content}</div>
                    <div className="message-time">
                      {new Date(comment.created_at).toLocaleString()}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <Form onSubmit={handleAddComment}>
            <div className="d-flex gap-2">
              <Form.Control
                type="text"
                placeholder="Add a comment..."
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
              />
              <Button type="submit" variant="primary">
                Send
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default BrainstormingSession;